
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import { DateTime } from 'luxon';
import DatePicker from '@/components/shared/DatePicker.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import SelectComponent from '@/components/shared/Select.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'orders';

@Component<QuoteInformation>({
	components: {
		DatePicker,
		InfoBanner,
		SelectComponent,
		TextField,
		ValidationProvider
	}
})
export default class QuoteInformation extends Vue {
	@Action('setCheckoutAutoAccept', { namespace }) private setCheckoutAutoAccept!: (autoAccept: boolean) => void;
	@Action('setQuoteInformation', { namespace }) private setQuoteInformation!: (quoteBehavior: QuoteInfo | null) => void;
	@Getter('getTotalWithTip', { namespace }) private totalWithTip!: string;
	@Getter('getQuoteInformation', { namespace }) private checkoutQuoteInfo!: QuoteInfo;
	@Getter('getEventInformation', { namespace }) private event!: EventInfo | null;
	@Getter('getTimezone', { namespace: 'auth' }) private timezone!: string;
	@Prop({ type: Boolean, default: false }) private hideHeader!: boolean;
	@Watch('eventDate')
	private onEventDateChange(newDate: string) {
		if (newDate && this.quoteInfo.quoteExpiresAt) {
			const eventDateObj = DateTime.fromISO(newDate);
			const expiryDate = DateTime.fromISO(this.quoteInfo.quoteExpiresAt);
			if (eventDateObj <= expiryDate) {
				this.quoteInfo.quoteExpiresAt = '';
				this.setQuoteInformation(this.quoteInfo);
			}
		}
	}

	private quoteInfo: QuoteInfo = {
		quoteBehavior: 'noAction',
		type: 'dollar',
		amount: null,
		percentageValue: null,
		quoteExpiresAt: ''
	};

	private get eventDate(): string {
		if (this.event?.eventStartDate) {
			return DateTime.fromISO(this.event.eventStartDate).toISODate();
		}
		return '';
	}

	private get quoteActionOptions(): { text: string; value: string }[] {
		return [
			{ text: this.$t('orders.guest_details.quote_details.require_deposit'), value: 'requireDeposit' },
			{ text: this.$t('orders.guest_details.quote_details.require_approval'), value: 'requireApproval' },
			{ text: this.$t('orders.guest_details.quote_details.mark_accepted'), value: 'autoAccept' },
			{ text: this.$t('orders.guest_details.quote_details.no_action'), value: 'noAction' }
		];
	}

	/**
	 * Short timezone abbreviation for the restaurant's timezone (e.g. "EST", "PST").
	 *
	 * @return {string}
	 */
	private get timezoneAbbreviation(): string {
		return DateTime.now().setZone(this.timezone).toFormat('ZZZZ');
	}

	/**
	 * Minimum selectable expiry date (tomorrow).
	 *
	 * @return {string}
	 */
	private get minExpiryDate(): string {
		return DateTime.now().setZone(this.timezone).plus({ days: 1 }).toISODate();
	}

	private created(): void {
		if (this.checkoutQuoteInfo) {
			this.quoteInfo = { ...this.checkoutQuoteInfo };
		}
		else {
			this.setQuoteInformation(this.quoteInfo);
		}
	}

	/**
	 * Handle quote action change, reset irrelevant fields, and update the store.
	 *
	 * @return {void}
	 */
	private handleActionChange(): void {
		this.setCheckoutAutoAccept(this.quoteInfo.quoteBehavior === 'autoAccept');
		if (this.quoteInfo.quoteBehavior !== 'requireDeposit') {
			this.quoteInfo.amount = null;
			this.quoteInfo.percentageValue = null;
		}
		if (this.quoteInfo.quoteBehavior !== 'requireDeposit' && this.quoteInfo.quoteBehavior !== 'requireApproval') {
			this.quoteInfo.quoteExpiresAt = '';
		}
		this.setQuoteInformation(this.quoteInfo);
	}

	/**
	 * Handle deposit amount input and calculate the corresponding percentageValue or amount.
	 *
	 * @param {number} value
	 * @return {void}
	 */
	private handleAmountChange(value: number): void {
		if (this.quoteInfo.type === 'percentage') {
			this.quoteInfo.percentageValue = +value;
			this.quoteInfo.amount = +(+this.totalWithTip * (value / 100)).toFixed(2);
		}
		else {
			this.quoteInfo.amount = +value;
			this.quoteInfo.percentageValue = null;
		}
		this.setQuoteInformation(this.quoteInfo);
	}

	/**
	 * Reset amount fields when switching deposit type.
	 *
	 * @return {void}
	 */
	private handleDepositTypeChange(): void {
		this.quoteInfo.amount = null;
		this.quoteInfo.percentageValue = null;
		this.setQuoteInformation(this.quoteInfo);
	}

	/**
	 * Update the quote expiry date.
	 *
	 * @param {string} date
	 * @return {void}
	 */
	private handleQuoteDateChange(date: string): void {
		this.quoteInfo.quoteExpiresAt = date;
		this.setQuoteInformation(this.quoteInfo);
	}
}
